import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const ImportWebsitePage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  
  // État pour la liste des URLs
  const [urls, setUrls] = useState([]);
  const [loadingUrls, setLoadingUrls] = useState(true);
  
  // État pour le formulaire
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    include: true,
    freshness: 30
  });
  // Filtres d'affichage
  const [hideOnce, setHideOnce] = useState(false);
  const [hideExcluded, setHideExcluded] = useState(false);
  
  // État pour les messages
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        } else {
          setCurrentUser(user);
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
      } finally {
        setAuthChecked(true);
      }
    };
    
    getUser();
  }, []);

  // Récupérer les URLs
  const fetchUrls = async () => {
    setLoadingUrls(true);
    try {
      const { data, error } = await supabase
        .from('urls')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUrls(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des URLs:', error);
      setStatus('error');
      setMessage(t('settings.admin.importWebsite.loadError'));
    } finally {
      setLoadingUrls(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUrls();
    }
  }, [currentUser]);

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url.trim()) {
      setStatus('error');
      setMessage(t('settings.admin.importWebsite.urlRequired'));
      return;
    }

    try {
      if (isEditing && editingId) {
        // Mise à jour
        const { error } = await supabase
          .from('urls')
          .update({
            url: formData.url,
            include: formData.include,
            freshness: formData.freshness,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingId);

        if (error) throw error;
        setMessage(t('settings.admin.importWebsite.updateSuccess'));
      } else {
        // Création
        const { error } = await supabase
          .from('urls')
          .insert([{
            url: formData.url,
            include: formData.include,
            freshness: formData.freshness
          }]);

        if (error) throw error;
        setMessage(t('settings.admin.importWebsite.addSuccess'));
      }

      setStatus('success');
      setFormData({ url: '', include: true, freshness: 30 });
      setIsEditing(false);
      setEditingId(null);
      fetchUrls();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setStatus('error');
      setMessage(error.message || t('settings.admin.importWebsite.saveError'));
    }
  };

  // Éditer une URL
  const handleEdit = (url) => {
    setFormData({
      url: url.url,
      include: url.include,
      freshness: url.freshness
    });
    setIsEditing(true);
    setEditingId(url.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Annuler l'édition
  const handleCancel = () => {
    setFormData({ url: '', include: true, freshness: 30 });
    setIsEditing(false);
    setEditingId(null);
  };

  // Supprimer une URL
  const handleDelete = async (id) => {
    if (!window.confirm(t('settings.admin.importWebsite.confirmDelete'))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('urls')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setStatus('success');
      setMessage(t('settings.admin.importWebsite.deleteSuccess'));
      fetchUrls();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setStatus('error');
      setMessage(error.message || t('settings.admin.importWebsite.deleteError'));
    }
  };

  // Efface le message après 5 secondes
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
        setStatus('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // URLs filtrées pour l'affichage selon les options de filtre
  const filteredUrls = urls.filter(u => {
    if (hideOnce && Number(u.freshness) === 0) return false;
    if (hideExcluded && u.include === false) return false;
    return true;
  });

  if (adminLoading || !authChecked) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('settings.admin.importWebsite.title')} backPath="/settings" />

      <div className="p-4">
        {!currentUser ? (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
            <p className="text-amber-700">{t('common.pleaseLogin')}</p>
            <button 
              className="mt-2 bg-[#2894D8] text-white px-4 py-2 rounded hover:bg-[#136E9D] transition-colors"
              onClick={() => window.location.href = '/login'}
            >
              {t('common.login')}
            </button>
          </div>
        ) : (
          <>
            {/* Formulaire d'ajout/modification */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
              <h2 className="text-h2 mb-4">
                {isEditing ? t('settings.admin.importWebsite.editUrl') : t('settings.admin.importWebsite.addUrl')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* URL */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('settings.admin.importWebsite.url')}
                  </label>
                  <input 
                    type="url"
                    placeholder={t('settings.admin.importWebsite.urlPlaceholder')}
                    className="w-full p-2 border rounded-lg"
                    value={formData.url}
                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                    required
                  />
                </div>

                {/* Mode include/exclude */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('settings.admin.importWebsite.includeMode')}
                  </label>
                  <div className="flex items-center space-x-3">
                    <span className={`text-sm ${!formData.include ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                      {t('settings.admin.importWebsite.exclude')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, include: !formData.include })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2894D8] focus:ring-offset-2 ${
                        formData.include ? 'bg-[#2894D8]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.include ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-sm ${formData.include ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                      {t('settings.admin.importWebsite.include')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('settings.admin.importWebsite.includeModeHelp')}
                  </p>
                </div>

                {/* Freshness */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('settings.admin.importWebsite.freshness')}
                  </label>
                  <select 
                    className="w-full p-2 border rounded-lg"
                    value={formData.freshness}
                    onChange={e => setFormData({ ...formData, freshness: Number(e.target.value) })}
                  >
                    <option value={0}>{t('settings.admin.importWebsite.freshnessOnce')}</option>
                    <option value={7}>7 {t('settings.admin.importWebsite.days')}</option>
                    <option value={15}>15 {t('settings.admin.importWebsite.days')}</option>
                    <option value={30}>30 {t('settings.admin.importWebsite.days')}</option>
                    <option value={45}>45 {t('settings.admin.importWebsite.days')}</option>
                    <option value={60}>60 {t('settings.admin.importWebsite.days')}</option>
                    <option value={75}>75 {t('settings.admin.importWebsite.days')}</option>
                    <option value={90}>90 {t('settings.admin.importWebsite.days')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.admin.importWebsite.freshnessHelp')}
                  </p>
                </div>

                {/* Boutons */}
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-[#2894D8] text-white px-4 py-2 rounded hover:bg-[#136E9D] transition-colors"
                  >
                    {isEditing ? t('common.save') : t('settings.admin.importWebsite.add')}
                  </button>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                </div>
              </form>

              {message && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  status === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {message}
                </div>
              )}
            </div>

            {/* Liste des URLs */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-h2 mb-4">{t('settings.admin.importWebsite.existingUrls')}</h2>
              <div className="flex items-center space-x-4 mb-4">
                <label className="flex items-center text-sm space-x-2">
                  <input type="checkbox" className="form-checkbox h-4 w-4" checked={hideOnce} onChange={e => setHideOnce(e.target.checked)} />
                  <span>{t('settings.admin.importWebsite.hideOnce')}</span>
                </label>
                <label className="flex items-center text-sm space-x-2">
                  <input type="checkbox" className="form-checkbox h-4 w-4" checked={hideExcluded} onChange={e => setHideExcluded(e.target.checked)} />
                  <span>{t('settings.admin.importWebsite.hideExcluded')}</span>
                </label>
              </div>
              {loadingUrls ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2894D8]"></div>
                </div>
              ) : filteredUrls.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('settings.admin.importWebsite.noUrls')}
                </p>
              ) : (
                <div className="space-y-3">
                  {filteredUrls.map((url) => (
                    <div key={url.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm break-all">{url.url}</div>
                          <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                            <span className={`px-2 py-1 rounded ${
                              url.include 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {url.include ? t('settings.admin.importWebsite.include') : t('settings.admin.importWebsite.exclude')}
                            </span>
                            <span>{url.freshness} {t('settings.admin.importWebsite.days')}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-2">
                          <button
                            onClick={() => handleEdit(url)}
                            className="text-blue-600 hover:text-blue-800 px-2 py-1 text-sm"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(url.id)}
                            className="text-red-600 hover:text-red-800 px-2 py-1 text-sm"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportWebsitePage;
